import { useState } from 'react';
import PropTypes from 'prop-types';
import { Paneset, Loading, Layout } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import ViewReportBuilder from '../../components/reports/builders/shared/ViewReportBuilder';
import ResultsPreview from '../../components/reports/results/preview/ResultsPreview';
import SaveNewReportManager from '../../components/reports/management/save/SaveNewReportManager';
import useURLParams from '../../hooks/useURLParams';
import useCubeQuery from '../../hooks/useQuery';
import useReportContext from '../../hooks/useReportContext';

// ── Section config for circulation_view ───────────────────────────────────
const CIRCULATION_SECTIONS = [
  {
    formField:   'loanFields',
    filtersField: 'loanFilters',
    fieldType:   'dimension',
    label: (
      <FormattedMessage
        id="ui-reports.circulationReports.loanFields.title"
        defaultMessage="Loan Data"
      />
    ),
    description: (
      <FormattedMessage
        id="ui-reports.circulationReports.loanFields.description"
        defaultMessage="Select fields and filters for loan data:"
      />
    ),
    fields: [
      'circulation_view.loan_id',
      'circulation_view.loan_date',
      'circulation_view.due_date',
      'circulation_view.return_date',
      'circulation_view.loan_status',
      'circulation_view.loan_action',
      'circulation_view.renewal_count',
      'circulation_view.loan_created_date',
      'circulation_view.loan_updated_date',
    ],
  },
  {
    formField:   'patronFields',
    filtersField: 'patronFilters',
    fieldType:   'dimension',
    label: (
      <FormattedMessage
        id="ui-reports.circulationReports.patronFields.title"
        defaultMessage="Patron Data"
      />
    ),
    description: (
      <FormattedMessage
        id="ui-reports.circulationReports.patronFields.description"
        defaultMessage="Select fields and filters for patron data:"
      />
    ),
    fields: [
      'circulation_view.borrower_username',
      'circulation_view.borrower_first_name',
      'circulation_view.borrower_last_name',
      'circulation_view.borrower_email',
      'circulation_view.borrower_phone',
      'circulation_view.borrower_barcode',
      'circulation_view.patron_group_name',
      'circulation_view.borrower_active',
      'circulation_view.borrower_type',
      'circulation_view.borrower_enrollment_date',
      'circulation_view.borrower_expiration_date',
    ],
  },
  {
    formField:   'itemFields',
    filtersField: 'itemFilters',
    fieldType:   'dimension',
    label: (
      <FormattedMessage
        id="ui-reports.circulationReports.itemFields.title"
        defaultMessage="Item Data"
      />
    ),
    description: (
      <FormattedMessage
        id="ui-reports.circulationReports.itemFields.description"
        defaultMessage="Select fields and filters for item and bibliographic data:"
      />
    ),
    fields: [
      'circulation_view.title',
      'circulation_view.item_barcode',
      'circulation_view.item_hrid',
      'circulation_view.call_number',
      'circulation_view.material_type',
      'circulation_view.loan_type',
      'circulation_view.item_status',
      'circulation_view.location_name',
      'circulation_view.library_name',
      'circulation_view.campus_name',
      'circulation_view.institution_name',
      'circulation_view.contributors',
      'circulation_view.identifiers',
      'circulation_view.subjects',
      'circulation_view.resource_type',
    ],
  },
  {
    formField:   'measures',
    filtersField: 'measuresFilters',
    fieldType:   'measure',
    label: (
      <FormattedMessage
        id="ui-reports.circulationReports.measures.title"
        defaultMessage="Statistics & Measures"
      />
    ),
    description: (
      <FormattedMessage
        id="ui-reports.circulationReports.measures.description"
        defaultMessage="Select statistical measures and filters:"
      />
    ),
    fields: [
      'circulation_view.total_loans',
      'circulation_view.loans_created',
      'circulation_view.loans_updated',
      'circulation_view.total_renewals',
      'circulation_view.total_borrowers',
      'circulation_view.total_items',
    ],
  },
];

function CirculationReportsRoute(props) {
  const [formState, setFormState] = useURLParams('form_state', '');
  const hideBuilder = new URLSearchParams(props.location?.search).get('hide_builder') === 'true';
  const [showBuilderPane, setShowBuilderPane] = useState(!hideBuilder);
  const [showSavePane, setShowSavePane]       = useState(false);
  const [currentSaveMode, setCurrentSaveMode] = useState('new');
  const [currentQuery, setCurrentQuery]       = useState('');
  const [queryOptions, setQueryOptions]       = useState({});
  const [executionCount, setExecutionCount]   = useState(0);
  const [resultSet, error, isLoading]         = useCubeQuery(currentQuery, queryOptions);

  const {
    reportId, reportName, reportDesc,
    isRunMode, isEditMode,
    defaultDisplayMethod: savedDefaultDisplay,
    displayMethods: savedDisplayMethods,
    returnSearch, isContextLoading,
  } = useReportContext(props.location);

  const [displayMethod, setDisplayMethod] = useURLParams('dm', savedDefaultDisplay);
  const isReportMode = (isRunMode || isEditMode) && reportId;
  const hasChanges   = isReportMode && (isEditMode || executionCount > 1);

  const handleClose = () => {
    if (isReportMode && reportId) {
      props.history.push({ pathname: `/reports/reports-list/${reportId}/view`, search: returnSearch || '' });
    } else {
      props.history.push('/reports/report-builder');
    }
  };

  if (isContextLoading) {
    return (
      <Layout className="centered full" style={{ height: '8rem' }}>
        <Loading size="xlarge" />
      </Layout>
    );
  }

  return (
    <Paneset>
      <ViewReportBuilder
        viewName="circulation_view"
        sections={CIRCULATION_SECTIONS}
        timeDimension="circulation_view.loan_date"
        builderTitle={
          <FormattedMessage
            id="ui-reports.circulationReports.title"
            defaultMessage="Circulation Report Builder"
          />
        }
        showBuilderPane={showBuilderPane}
        setShowBuilderPane={setShowBuilderPane}
        handleClose={handleClose}
        formState={formState}
        setFormState={setFormState}
        setQuery={(query, options = {}) => {
          setCurrentQuery(query);
          setQueryOptions(options);
          setExecutionCount(prev => prev + 1);
        }}
        isLoading={isLoading}
      />

      <ResultsPreview
        showQueryBuilderPane={showBuilderPane}
        setShowQueryBuilderPane={setShowBuilderPane}
        showSavePane={showSavePane}
        setShowSavePane={setShowSavePane}
        handleClose={handleClose}
        query={currentQuery}
        resultSet={resultSet}
        error={error}
        isLoading={isLoading}
        reportType="statistical"
        displayMethod={displayMethod}
        setDisplayMethod={setDisplayMethod}
        savedDisplayMethods={savedDisplayMethods}
        reportName={reportName}
        reportDesc={reportDesc}
        reportId={reportId}
        isRunMode={isRunMode || false}
        isEditMode={isEditMode || false}
        hasChanges={hasChanges}
        onOpenSavePane={mode => { setCurrentSaveMode(mode); setShowBuilderPane(false); setShowSavePane(true); }}
        isContextLoading={isContextLoading}
      />

      <SaveNewReportManager
        {...props}
        showQueryBuilderPane={showBuilderPane}
        setShowQueryBuilderPane={setShowBuilderPane}
        showSavePane={showSavePane}
        setShowSavePane={setShowSavePane}
        handleClose={handleClose}
        query={currentQuery}
        reportType="statistical"
        builderType="circulation"
        builderState={formState}
        saveMode={currentSaveMode}
        savedReportId={reportId}
        savedReportName={reportName}
        savedReportDesc={reportDesc}
      />
    </Paneset>
  );
}

CirculationReportsRoute.propTypes = {
  history:  PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.object,
};

export default CirculationReportsRoute;
